import { useTranslation } from 'react-i18next';

import { Button } from '@mui/material';

import { getErrorKind } from '@/shared/lib/errors';
import { ErrorPage, ReloadButton, RetryButton, errorKindToVariant } from '@/shared/ui/ErrorPage';

interface Props {
  error: unknown;
  fullScreen?: boolean;
  // Without onRetry the primary action reloads the page.
  onRetry?: () => void;
  onGoHome?: () => void;
}

// The page a boundary shows for anything that is not a 404/403: crashes, server
// and network failures, and a stale build whose chunks are gone. Uses no router
// or app context, so the root boundary can show it when the providers crashed.
function AppCrashPage({ error, fullScreen, onRetry, onGoHome }: Props) {
  const { t } = useTranslation();
  const variant = errorKindToVariant(getErrorKind(error));

  // A failed chunk import is cached by React.lazy; only a reload fetches the new build.
  const isStaleBuild = variant === 'updated';
  // Retrying a 403 cannot succeed.
  const canRetry = Boolean(onRetry) && !isStaleBuild && variant !== 'forbidden';

  return (
    <ErrorPage
      variant={variant}
      error={error}
      fullScreen={fullScreen}
      actions={
        <>
          {canRetry && onRetry ? <RetryButton onRetry={onRetry} /> : <ReloadButton />}
          {onGoHome && !isStaleBuild && (
            <Button variant="text" data-testid="error-page-home-button" onClick={onGoHome}>
              {t('error_page.actions.go_home')}
            </Button>
          )}
        </>
      }
    />
  );
}

export default AppCrashPage;

export const goToAppRoot = () => window.location.assign(window.location.origin);
